import { Controller } from "@hotwired/stimulus"

// stats--entity on `app/views/stats/entity_stat.haml` (#entity-stat).
export default class extends Controller {
  static targets = [
    "statDataSelect",
    "sdkDataSelect",
    "osDataSelect",
    "apiCallsChart",
    "sdkVerChart",
    "osVerChart",
    "accountsTable",
    "dateFrom",
    "dateTo",
  ]

  static values = {
    accountId: String,
    graphData: String,
    appsSeries: Array,
  }

  connect() {
    $("#main-nav").removeClass("selected").find(".stats").addClass("selected")

    this.currentGraphData = this.graphDataValue

    Highcharts.setOptions({ global: { useUTC: true } })

    if (this.hasAccountsTableTarget) this.#initAccountsTable()
    if (this.hasApiCallsChartTarget) this.#initApiCallsChart()
    if (this.hasSdkVerChartTarget) this.#initSdkVerChart()
    if (this.hasOsVerChartTarget) this.#initOsVerChart()
    this.#initPeriodDatepickers()

    if (this.hasStatDataSelectTarget) {
      $(this.statDataSelectTarget).on("change.statsEntity", () => this.#onStatDataChange())
    }
    if (this.hasSdkDataSelectTarget) {
      $(this.sdkDataSelectTarget).on("change.statsEntity", () => this.#onSdkDataChange())
    }
    if (this.hasOsDataSelectTarget) {
      $(this.osDataSelectTarget).on("change.statsEntity", () => this.#onOsDataChange())
    }
  }

  disconnect() {
    $("#main-nav .stats").removeClass("selected")

    if (this.hasStatDataSelectTarget) $(this.statDataSelectTarget).off(".statsEntity")
    if (this.hasSdkDataSelectTarget) $(this.sdkDataSelectTarget).off(".statsEntity")
    if (this.hasOsDataSelectTarget) $(this.osDataSelectTarget).off(".statsEntity")

    if (this.accountsDataTable) {
      this.accountsDataTable.destroy()
      this.accountsDataTable = null
    }
    if (this.apiChart) {
      this.apiChart.destroy()
      this.apiChart = null
    }
    if (this.sdkChart) {
      this.sdkChart.destroy()
      this.sdkChart = null
    }
    if (this.osChart) {
      this.osChart.destroy()
      this.osChart = null
    }
    if (this.hasDateFromTarget && $(this.dateFromTarget).data("xdsoft_datetimepicker")) {
      $(this.dateFromTarget).datetimepicker("destroy")
    }
    if (this.hasDateToTarget && $(this.dateToTarget).data("xdsoft_datetimepicker")) {
      $(this.dateToTarget).datetimepicker("destroy")
    }
  }

  showStatsForPeriod(event) {
    event.preventDefault()
    if (!this.#validateTimePeriod()) return

    this.apiChart.series.forEach((serie) => {
      serie.setData([], true)
    })
    this.apiChart.showLoading()
    this.#requestApiData()
  }

  #onStatDataChange() {
    this.currentGraphData = this.statDataSelectTarget.value
    this.statDataSelectTarget.disabled = true
    this.apiChart.series.forEach((serie) => {
      serie.setData([], true)
    })
    this.apiChart.showLoading()
    this.#requestApiData()
  }

  #onSdkDataChange() {
    this.sdkDataSelectTarget.disabled = true
    this.#clearSeries(this.sdkChart)
    this.sdkChart.showLoading()
    this.#requestSdkOs(this.sdkDataSelectTarget)
  }

  #onOsDataChange() {
    this.osDataSelectTarget.disabled = true
    this.#clearSeries(this.osChart)
    this.osChart.showLoading()
    this.#requestSdkOs(this.osDataSelectTarget)
  }

  #initAccountsTable() {
    this.accountsDataTable = $(this.accountsTableTarget).DataTable({
      dom: '<"datawrap" t><"F">',
      processing: false,
      serverSide: false,
      ordering: false,
      stateSave: false,
      autoWidth: false,
      initComplete() {
        $("#loading").hide()
        $("#workspace").show()
      },
      columnDefs: [{ targets: [0, 1, 2, 3, 4, 5, 6], orderable: false }],
    })
  }

  #initApiCallsChart() {
    const controller = this

    this.apiChart = new Highcharts.Chart({
      chart: {
        animation: false,
        renderTo: this.apiCallsChartTarget,
        type: "spline",
        events: {
          load() {
            controller.#requestApiData()
          },
        },
      },
      title: { text: "" },
      xAxis: { type: "datetime" },
      yAxis: { min: 0, title: { text: "Count" } },
      series: this.appsSeriesValue,
    })
    this.apiChart.showLoading()
  }

  #initSdkVerChart() {
    const controller = this

    this.sdkChart = new Highcharts.Chart({
      chart: {
        renderTo: this.sdkVerChartTarget,
        type: "spline",
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        events: {
          load() {
            controller.#requestSdkOs(controller.sdkDataSelectTarget)
          },
        },
      },
      title: { text: "SDK versions" },
      xAxis: { type: "datetime", tickmarkPlacement: "on", title: { enabled: false } },
      yAxis: { title: { text: "API Calls count" } },
      tooltip: {
        formatter() {
          let str = `${this.series.name}: ${Highcharts.numberFormat(this.y, 0, ",")} calls`
          if (this.percentage > 0) {
            str += ` (${Highcharts.numberFormat(this.percentage, 1)}%)`
          }
          return str
        },
      },
      plotOptions: {
        area: {
          stacking: "percent",
          lineColor: "#ffffff",
          lineWidth: 1,
          marker: { lineWidth: 1, lineColor: "#ffffff" },
        },
      },
      series: [],
    })
    this.sdkChart.showLoading()
  }

  #initOsVerChart() {
    const controller = this

    this.osChart = new Highcharts.Chart({
      chart: {
        renderTo: this.osVerChartTarget,
        type: "spline",
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        events: {
          load() {
            controller.#requestSdkOs(controller.osDataSelectTarget)
          },
        },
      },
      title: { text: "OS versions" },
      xAxis: { type: "datetime", tickmarkPlacement: "on", title: { enabled: false } },
      yAxis: { title: { text: "API Calls count" } },
      tooltip: {
        formatter() {
          let str = `${this.series.name}: ${Highcharts.numberFormat(this.y, 0, ",")} calls`
          if (this.percentage > 0) {
            str += ` (${Highcharts.numberFormat(this.percentage, 1)}%)`
          }
          return str
        },
      },
      plotOptions: {
        area: {
          stacking: "percent",
          lineColor: "#ffffff",
          lineWidth: 1,
          marker: { lineWidth: 1, lineColor: "#ffffff" },
        },
      },
      series: [],
    })
    this.osChart.showLoading()
  }

  #requestApiData() {
    if (!this.apiChart) return

    $.ajax({
      url: `get/${this.accountIdValue}?data=${this.currentGraphData}&range=${this.#dateRange()}`,
      success: (dataArr) => {
        if (!this.apiChart) return

        this.apiChart.series.forEach((currentSerie) => {
          const points = dataArr[currentSerie.name]
          if (points) {
            points.forEach((val) => {
              currentSerie.addPoint(val, true)
            })
          }
        })

        if (this.hasStatDataSelectTarget) this.statDataSelectTarget.disabled = false
        this.apiChart.hideLoading()
      },
      cache: false,
    })
  }

  #requestSdkOs(selectEl) {
    if (!selectEl) return

    const applicationId = selectEl.value
    const isSdk = selectEl === this.sdkDataSelectTarget
    const method = isSdk ? "sdk_versions" : "os_versions"
    const chart = isSdk ? this.sdkChart : this.osChart

    $.ajax({
      url: `get/${this.accountIdValue}?data=${method}&application_id=${applicationId}`,
      success: (dataArr) => {
        dataArr.forEach((retData) => {
          chart.addSeries({
            name: retData.serie_name,
            type: "line",
            data: retData.data,
          })
        })

        selectEl.disabled = false
        this.sdkChart?.hideLoading()
        this.osChart?.hideLoading()
      },
      cache: false,
    })
  }

  #clearSeries(chart) {
    while (chart.series.length > 0) {
      chart.series[0].remove(true)
    }
  }

  #initPeriodDatepickers() {
    if (this.hasDateFromTarget) {
      $(this.dateFromTarget).datetimepicker({
        onGenerate: (dt) => {
          this.dateFromTarget.value = this.#formatDate(dt)
        },
        timepicker: false,
        format: "d.m.Y",
        formatDate: "d.m.Y",
        closeOnDateSelect: true,
        maxDate: this.#formatDate(new Date()),
        value: moment().add(-1, "months").format("DD.MM.YYYY"),
        mask: true,
      })
    }

    if (this.hasDateToTarget) {
      $(this.dateToTarget).datetimepicker({
        onGenerate: (currentDateTime) => {
          this.dateToTarget.value = this.#formatDate(currentDateTime)
        },
        onShow: function () {
          this.setOptions({
            value: $("#datepicker_stat_to").val(),
          })
        },
        timepicker: false,
        format: "d.m.Y",
        formatDate: "d.m.Y",
        closeOnDateSelect: true,
        maxDate: this.#formatDate(new Date()),
        mask: true,
      })
    }
  }

  #validateTimePeriod() {
    const statFrom = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const statTo = this.hasDateToTarget ? this.dateToTarget.value : ""

    const timeFrom = Date.parseDate(statFrom, "d.m.Y")
    const timeTo = Date.parseDate(statTo, "d.m.Y")

    if (timeFrom > timeTo) {
      alert("Incorrectly selected time period statistics.")
      return false
    }
    return true
  }

  #dateRange() {
    const from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const to = this.hasDateToTarget ? this.dateToTarget.value : ""
    return `${from}:${to}`
  }

  #formatDate(date) {
    let dd = date.getDate()
    let mm = date.getMonth() + 1
    const yy = date.getFullYear()
    if (dd < 10) dd = `0${dd}`
    if (mm < 10) mm = `0${mm}`
    return `${dd}.${mm}.${yy}`
  }
}
